import {
  Box,
  Button,
  Divider,
  TextField,
  Typography,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Collapse,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import { useState } from "react";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

const Form = (props) => {
  const {
    formTitle,
    formObject = {},
    setFormObject,
    columns = [],
    setShowForm,
    object,
    submitButtonText,
    onSubmit,
  } = props;
  const theme = useTheme();

  const [collapseStatus, setCollapseStatus] = useState({});

  const setNestedValue = (obj, path, value) => {
    const keys = path.split('.');
    let current = obj;
    for (let i = 0; i < keys.length - 1; i++) {
      if (!current[keys[i]] || typeof current[keys[i]] !== 'object') {
        current[keys[i]] = {};
      }
      current = current[keys[i]];
    }
    current[keys[keys.length - 1]] = value;
    return obj;
  };

  const handleChange = (id, value, editable = true) => {
    if (!editable) return;

    setFormObject((prev) => {
      const newFormObject = JSON.parse(JSON.stringify(prev));

      if (id.includes('.')) {
        setNestedValue(newFormObject, id, value);
        return newFormObject;
      }
      
      if (id === `${object}GeoJson`) {
        return {
          ...prev,
          [`${object}GeoJson`]: value,
          [`${object}Latitude`]: value?.geometry?.coordinates[1] || prev[`${object}Latitude`],
          [`${object}Longitude`]: value?.geometry?.coordinates[0] || prev[`${object}Longitude`],
          [`${object}Altitude`]: value?.properties?.altitude || prev[`${object}Altitude`],
        };
      }

      if (id === `${object}Latitude` || id === `${object}Longitude`) {
        const coordinates = prev[`${object}GeoJson`]?.geometry?.coordinates || [0, 0];
        const newCoordinates = [
          id === `${object}Longitude` ? value : coordinates[0],
          id === `${object}Latitude` ? value : coordinates[1],
        ];

        return {
          ...prev,
          [id]: value,
          [`${object}GeoJson`]: {
            ...prev[`${object}GeoJson`],
            geometry: {
              ...prev[`${object}GeoJson`]?.geometry,
              coordinates: newCoordinates,
            },
          },
        };
      }
      return { ...prev, [id]: value };
    });
  };

  const getNestedValue = (obj, path) => {
    const keys = path.split('.');
    let value = obj;
    for (const key of keys) {
      if (value && typeof value === 'object' && key in value) {
        value = value[key];
      } else {
        return "";
      }
    }
    return value;
  };
  
  const renderField = (column) => {
    const { id, type, label, placeholder, height, editable, options, children } = column;

    const commonTextFieldStyles = {
      "& .MuiOutlinedInput-root": {
        backgroundColor: theme.palette.tertiary.main,
        borderRadius: 2,
        "& input": {
          padding: "8px",
          fontSize: "14px",
          "&::placeholder": {
            fontSize: "13px",
          },
          "&::-webkit-calendar-picker-indicator": {
            filter: theme.palette.mode === "dark" ? "invert(1)" : "invert(0)",
            cursor: "pointer",
          },
        },
        "& textarea": {
          padding: "7px",
          boxSizing: "border-box",
          height: "100%",
          lineHeight: "1.5",
          "&::placeholder": {
            fontSize: "13px",
          },
        },
      },
      "&.Mui-focused .MuiOutlinedInput-root": {
        border: `1px solid ${theme.palette.border.color}`,
      },
      "& .MuiOutlinedInput-notchedOutline": {
        padding: 1,
        border: `1px solid ${theme.palette.border.color} !important`,
        borderRadius: 2,
      },
    };

    switch (type) {
      case "textfield":
      case "number":
        return (
          <TextField
            fullWidth
            type={type === "number" ? "number" : "text"}
            placeholder={`Enter ${label}`}
            value={getNestedValue(formObject, id)}
            onChange={(e) => handleChange(id, e.target.value, editable)}
            sx={commonTextFieldStyles}
          />
        );
      case "description":
        return (
          <TextField
            fullWidth
            multiline
            placeholder={`Enter ${label}`}
            value={getNestedValue(formObject, id)}
            onChange={(e) => handleChange(id, e.target.value, editable)}
            sx={{
              ...commonTextFieldStyles,
              "& .MuiOutlinedInput-root": {
                ...commonTextFieldStyles["& .MuiOutlinedInput-root"],
                height: height || "100px",
                overflow: "auto",
                padding: "0px",
                alignItems: "flex-start",
              },
            }}
          />
        );
      case "toggle":
        return (
          <FormControlLabel
            control={
              <Switch
                checked={!!getNestedValue(formObject, id)}
                onChange={(e) => handleChange(id, e.target.checked, editable)}
                color="primary"
              />
            }
            label={label}
            labelPlacement="start"
            sx={{
              justifyContent: "space-between",
              marginLeft: 0,
              width: "100%",
              "& .MuiTypography-root": {
                fontSize: "13px",
                color: theme.palette.text.color,
                opacity: 0.9,
              },
              '& .MuiSwitch-switchBase.Mui-checked': {
                color: '#4CAF50',
              },
              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                backgroundColor: '#4CAF50',
              },
            }}
          />
        );
      case "dropdown":
        return (
          <Select
            value={getNestedValue(formObject, id) ?? ""}
            onChange={(e) => handleChange(id, e.target.value, editable)}
            displayEmpty
            fullWidth
            label={label}
            sx={{
              borderRadius: "8px",
              fontSize: "14px",
              textAlign: "left",
              backgroundColor: theme.palette.tertiary.main,
              color: theme.palette.text.color,
              "& .MuiOutlinedInput-notchedOutline": {
                border: `1px solid ${theme.palette.border.color}`,
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                border: `1px solid ${theme.palette.border.color}`,
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                border: `1px solid ${theme.palette.border.color}`,
              },
              "& .MuiSvgIcon-root": { color: theme.palette.text.color },
              "& .MuiSelect-select": {
                padding: "8px",
              },
              "& .MuiOutlinedInput-input": {
                padding: "8px",
              },
            }}
          >
            <MenuItem value="" disabled sx={{ fontSize: "14px" }}>
              {placeholder || "Select Status"}
            </MenuItem>
            {options?.map((option) => (
              <MenuItem key={option.value} value={option.value} sx={{ fontSize: "14px" }}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        );
      case "date":
        return (
          <TextField
            type="date"
            fullWidth
            value={getNestedValue(formObject, id) || ""}
            onChange={(e) => handleChange(id, e.target.value, editable)}
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: theme.palette.tertiary.main,
                borderRadius: 2,
                "& input": {
                  padding: "7px",
                  "&::placeholder": {
                    fontSize: "13px",
                  },
                  "&::-webkit-calendar-picker-indicator": {
                    filter: theme.palette.mode === "dark" ? "invert(1)" : "invert(0)",
                    cursor: "pointer",
                  },
                },
              },
              "&.Mui-focused .MuiOutlinedInput-root": {
                border: `1px solid ${theme.palette.border.color}`,
              },
              "& .MuiOutlinedInput-notchedOutline": {
                padding: 1,
                border: `1px solid ${theme.palette.border.color} !important`,
                borderRadius: 2,
              },
            }}
          />
        );

      case "time":
        return (
          <TextField
            type="time"
            fullWidth
            value={getNestedValue(formObject, id) || ""}
            onChange={(e) => handleChange(id, e.target.value, editable)}
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: theme.palette.tertiary.main,
                borderRadius: 2,
                "& input": {
                  padding: "7px",
                  "&::placeholder": {
                    fontSize: "13px",
                  },
                  "&::-webkit-calendar-picker-indicator": {
                    filter: theme.palette.mode === "dark" ? "invert(1)" : "invert(0)",
                    cursor: "pointer",
                  },
                },
              },
              "&.Mui-focused .MuiOutlinedInput-root": {
                border: `1px solid ${theme.palette.border.color}`,
              },
              "& .MuiOutlinedInput-notchedOutline": {
                padding: 1,
                border: `1px solid ${theme.palette.border.color} !important`,
                borderRadius: 2,
              },
            }}
          />
        );
        case "group":
        return (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              border: `1px solid ${theme.palette.border.color}`,
              borderRadius: "8px",
              padding: 2,
            }}
          >
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    cursor: "pointer",
                }}
                onClick={() => setCollapseStatus((prev) => ({ ...prev, [id]: !prev[id] }))}
            >
                <Typography
                sx={{
                    fontSize: 14,
                    fontWeight: 600,
                    color: theme.palette.text.color,
                    opacity: 0.9,
                    textAlign: "left",
                }}
                >
                {label}
                </Typography>
                {collapseStatus[id] ? <ExpandMoreIcon /> : <ChevronRightIcon />}
            </Box>
            <Collapse in={!collapseStatus[id]}>
                <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                }}
                >
                {children?.map((child) => (
                    <Box key={child.id}>
                    <Typography
                        sx={{
                        fontSize: 13,
                        color: theme.palette.text.color,
                        opacity: 0.9,
                        textAlign: "left",
                        margin: "2px 0px",
                        }}
                    >
                        {child.label}
                    </Typography>
                    {renderField(child)}
                    </Box>
                ))}
                </Box>
            </Collapse>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Box
      sx={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        zIndex: 10,
         '&::-webkit-scrollbar': {
      display: 'none', // Chrome, Safari, Opera
    },
      }}
    >
      <Box
        onClick={() => setShowForm(false)}
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          backgroundColor: "rgba(0, 0, 0, 0.5)",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          top: 0,
          right: 0,
          width: "350px",
          height: "100%",
          bgcolor: theme.palette.secondary.main,
          boxSizing: "border-box",
          p: 2,
          zIndex: 11,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            height: "100%",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              height: "100%",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                color: theme.palette.text.color,
                width: "100%",
              }}
            >
              <Typography
                sx={{
                  fontSize: 22,
                  fontWeight: 400,
                }}
              >
                {formTitle}
              </Typography>
              <CloseIcon
                sx={{
                  cursor: "pointer",
                  fontSize: 24,
                  padding: "4px",
                  borderRadius: 2,
                  border: `1px solid ${theme.palette.border.color}`,
                }}
                onClick={() => setShowForm(false)}
              />
            </Box>
            <Divider
              sx={{
                width: "100%",
                background: theme.palette.border.main,
                margin: "13px 0px",
              }}
            />

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                height: "100%",
                overflow: "auto",
                margin: "8px 0px",
                gap: 2,
                '&::-webkit-scrollbar': {
                  display: 'none',
                },
              }}
            >
              {columns?.map(
                (column) =>
                  column?.display !== false && (
                    <Box key={column.id}>
                      {column.type !== "toggle" && column.type !== "group" && (
                        <Typography
                          sx={{
                            fontSize: 13,
                            color: theme.palette.text.color,
                            opacity: 0.9,
                            textAlign: "left",
                            margin: "2px 0px",
                          }}
                        >
                          {column.label}
                        </Typography>
                      )}
                      {renderField(column)}
                    </Box>
                  )
              )}
              <Typography
                sx={{
                  display: "flex",
                  width: "fit-content",
                  justifyContent: "flex-end",
                  color: "#006BCE",
                  textDecoration: "underline",
                  cursor: "pointer",
                }}
              >
                Edit this form
              </Typography>
            </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              width: "100%",
            }}
          >
            <Button
              sx={{
                width: "49%",
                background: "transparent",
                border: "1px solid #006BCE",
                color: "#006BCE",
                textTransform: "none",
              }}
              onClick={() => {
                setShowForm(false);
              }}
            >
              Cancel
            </Button>
            <Button
              sx={{
                width: "49%",
                background: "#006BCE",
                color: "#FFFFFF",
                textTransform: "none",
                "&:hover": {
                  background: "#0058A5",
                },
                "&:disabled": {
                  background: "#666",
                  color: "#999",
                },
              }}
              onClick={onSubmit}
            >
              {submitButtonText}
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Form;